import { mobileDisplayPolicies } from "../mobile/displayPolicies.ts";

export type StandingsGapState =
  | {
      kind: "inPlayIn" | "inPlayoffs" | "topSeed";
      spots: 0;
    }
  | {
      kind: "outsidePlayoffs" | "fromFirst";
      spots: number;
    };

export const standingsCompactFieldLimit = mobileDisplayPolicies.standingsTeam.maxPrimaryFields;

export const getStandingsGapState = ({
  maxPlayoffSeed,
  maxPlayoffSeedNoPlayIn,
  playIn,
  rank,
}: {
  maxPlayoffSeed: number;
  maxPlayoffSeedNoPlayIn: number;
  playIn: boolean;
  rank: number;
}): StandingsGapState => {
  if (maxPlayoffSeed <= 0) {
    const spots = Math.max(0, rank - 1);
    return spots === 0
      ? {
          kind: "topSeed",
          spots,
        }
      : {
          kind: "fromFirst",
          spots,
        };
  }

  if (rank <= maxPlayoffSeedNoPlayIn) {
    return {
      kind: "inPlayoffs",
      spots: 0,
    };
  }

  if (playIn && rank <= maxPlayoffSeed) {
    return {
      kind: "inPlayIn",
      spots: 0,
    };
  }

  return {
    kind: "outsidePlayoffs",
    spots: Math.max(1, rank - maxPlayoffSeed),
  };
};

export const getStandingsCompactVisibleFields = <T>(
  fields: readonly T[],
  limit = standingsCompactFieldLimit,
) => fields.slice(0, limit);

export const getNextExpandedStandingsTid = (currentTid: number | undefined, nextTid: number) =>
  currentTid === nextTid ? undefined : nextTid;

export type StandingsLineKind = "play-in" | "playoff";

export const getStandingsLineKind = ({
  maxPlayoffSeed,
  maxPlayoffSeedNoPlayIn,
  rowIndex,
}: {
  maxPlayoffSeed: number;
  maxPlayoffSeedNoPlayIn: number;
  rowIndex: number;
}): StandingsLineKind | undefined => {
  if (maxPlayoffSeedNoPlayIn > 0 && rowIndex === maxPlayoffSeedNoPlayIn - 1) {
    return "playoff";
  }

  if (maxPlayoffSeed > maxPlayoffSeedNoPlayIn && rowIndex === maxPlayoffSeed - 1) {
    return "play-in";
  }
};

export const getStandingsContextIndexes = ({
  separatorIndexes = [],
  teamCount,
  userIndex,
  visibleCount,
}: {
  separatorIndexes?: readonly number[];
  teamCount: number;
  userIndex: number;
  visibleCount: number;
}) => {
  if (teamCount <= 0) {
    return [];
  }

  const normalizedVisibleCount = Math.min(teamCount, Math.max(1, visibleCount));
  if (userIndex < 0) {
    return Array.from({ length: normalizedVisibleCount }, (_, index) => index);
  }

  const beforeUser = Math.floor((normalizedVisibleCount - 1) / 2);
  let start = Math.max(0, userIndex - beforeUser);
  let end = Math.min(teamCount, start + normalizedVisibleCount);
  start = Math.max(0, end - normalizedVisibleCount);

  const indexes = new Set<number>();
  for (let index = start; index < end; index += 1) {
    indexes.add(index);
  }

  for (const separatorIndex of separatorIndexes) {
    if (separatorIndex >= 0 && separatorIndex < teamCount) {
      indexes.add(separatorIndex);
    }
  }

  return [...indexes].sort((a, b) => a - b);
};

export const getCollapsedStandingsTeams = <T extends { tid: number }>({
  canCollapse,
  expanded,
  separatorIndexes,
  teams,
  userTid,
  visibleCount,
}: {
  canCollapse: boolean;
  expanded: boolean;
  separatorIndexes?: readonly number[];
  teams: readonly T[];
  userTid: number;
  visibleCount: number;
}) => {
  if (!canCollapse || expanded) {
    return [...teams];
  }

  const userIndex = teams.findIndex((t) => t.tid === userTid);
  return getStandingsContextIndexes({
    separatorIndexes,
    teamCount: teams.length,
    userIndex,
    visibleCount,
  }).map((index) => teams[index]!);
};

export const standingsQualificationAppliesToScope = ({
  confCount,
  playoffsByConf,
  type,
}: {
  confCount: number;
  playoffsByConf: boolean;
  type: "league" | "conf";
}) => {
  if (type === "league") {
    return !playoffsByConf;
  }

  return playoffsByConf || confCount === 1;
};
